import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import Image from '../assets/images/ddf 1.png';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Link,
  styled,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';

const SignupForm = () => {
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();

  const { isLogin } = useAuth();

  useEffect(() => {
    if (isLogin) {
      navigate('/detail');
    }
  }, [isLogin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let token = params.get('token') || localStorage.getItem('token');

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const id = decoded.email;
        if (!id) {
          setError('Invalid token, no user ID found.');
          return;
        }
        setUserId(id);

        axios
          .get(`${api}/user/details/${id}`)
          .then((res) => {
            setEmail(res.data[0].email);
            setName(res.data[0].name);
            if (res.data[0].password) {
              navigate('/detail');
            }
          })
          .catch(() => {
            setError('Failed to fetch user details. Please try again.');
          });
      } catch (error) {
        setError('Invalid token.');
      }
    } else {
      setError('Token missing. Please try again.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      await axios.put(`${api}/user/signup/${userId}`, {
        name,
        password,
      });
      posthog.identify(userId, { name });
      posthog.capture('signup_completed', { name });
      navigate('/detail');
    } catch (err) {
      const msg = err.response?.data?.msg || 'Signup failed. Please try again.';
      setError(msg);
      posthog.captureException(err, { extra: { context: 'signup_completed' } });
    }
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      {/* Left form section */}
      <Grid
        item
        size={{ xs: 12, md: 6 }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 },
        }}
      >
        <Box>
          <Typography
            variant="h2"
            sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}
          >
            Sign up
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth={false}
              label="Name"
              variant="outlined"
              margin="normal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ width: '60%' }}
            />

            <TextField
              fullWidth
              label="Email id"
              variant="outlined"
              margin="normal"
              value={email}
              InputProps={{ readOnly: true }}
              disabled
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              // placeholder="+6 Character"
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              variant="outlined"
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <FormControlLabel
              control={<Checkbox size="small" required color="success" />}
              label={
                <Typography variant="body2">
                  I’ve read and agree with the Terms and Service and our{' '}
                  <WhiteLink href="/home/Company/Privacy_Policy">
                    Privacy Policy
                  </WhiteLink>
                </Typography>
              }
              sx={{
                mt: 1,
                '& .MuiFormControlLabel-asterisk': {
                  display: 'none',
                },
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              color="secondary"
              type="submit"
              fullWidth={false}
              sx={{
                mt: 3,
                py: 1.5,
                width: '60%',
                fontWeight: 600,
                borderRadius: '50px',
              }}
            >
              Sign Up
            </Button>
          </Box>
        </Box>
      </Grid>

      {/* Right image section (hidden on xs) */}
      <Grid
        item
        size={{ xs: false, md: 6 }}
        sx={{
          backgroundColor: '#fdf8f3',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box component="img" src={Image} alt="signup" sx={{ height: 'auto' }} />
      </Grid>
    </Grid>
  );
};

const WhiteLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'white' : 'black',
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.mode === 'dark' ? 'white' : 'black',
    textDecoration: 'underline',
  },
}));

export default SignupForm;
